import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { cardMessages } from 'src/features/cards/utils/messages';
import { CellAllowedDays } from 'src/features/memberships/components/table/cells/CellAllowedDays';
import { badgeAllowedDaysOptions, END_FULL_DAY, START_FULL_DAY } from 'src/features/memberships/utils/constants';
import { membershipMessages } from 'src/features/memberships/utils/messages';
import { useListSubParkings } from 'src/features/subParkings/api/useListSubParkings';
import { useListPrepaidCards } from 'src/features/subscriptions/api/useListPrepaidCards';
import { useListPrepaidPlans } from 'src/features/subscriptions/api/useListPrepaidPlans';
import { DURATION_MAP, type PrepaidCard } from 'src/features/subscriptions/types';
import { badgeSubscriptionStatusOptions } from 'src/features/subscriptions/utils/constants';
import subscriptionMessages from 'src/features/subscriptions/utils/messages';
import useFilters from 'src/hooks/useFilters';
import usePagination from 'src/hooks/usePagination';
import useSearch from 'src/hooks/useSearch';
import useSorting from 'src/hooks/useSorting';
import { useParking } from 'src/providers/ParkingProvider';
import {
    CellDatetime,
    CellIndex,
    DataTable,
    DataTableColumnGenericHeaderDefinition,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import useRowSelection from 'src/registry/hooks/useRowSelection';
import { Badge } from 'src/registry/ui/Badge';

const VehiclesSubscriptionsTable = () => {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();
    const navigate = useNavigate();

    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'card_number' });
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'valid_from', desc: true }],
    });
    const { queryFiltersOptions, ...filters } = useFilters({
        initialQueryFilters: { validity_status: 'active,upcoming' },
    });
    const { ...rowSelection } = useRowSelection();

    const { data, refetch, isError, isLoading } = useListPrepaidCards({
        parkingId,
        ...queryPaginationOptions,
        ...querySearchOptions,
        ...querySortingOptions,
        ...queryFiltersOptions,
    });

    const { data: subParkings } = useListSubParkings({ parkingId });

    const { data: plansData } = useListPrepaidPlans({ parkingId });

    const columnHelper = createColumnHelper<PrepaidCard>();

    const columns = [
        columnHelper.display({
            header: DataTableColumnGenericHeaderDefinition,
            id: 'id',
            cell: CellIndex,
            maxSize: 45,
            meta: { title: '#' },
        }),
        columnHelper.accessor('owner_name', {
            header: DataTableColumnHeader,
            meta: {
                title: formatMessage(subscriptionMessages.customer),
            },
        }),
        columnHelper.accessor('card_number', {
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(subscriptionMessages.subscription),
            },
        }),
        columnHelper.accessor('purchase_plan_id', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                if (!row.original.purchase_plan_id || !plansData) return '-';

                const plan = plansData.results.find((plan) => plan.id === row.original.purchase_plan_id);
                return plan?.plan_name || '-';
            },
            meta: {
                title: formatMessage(subscriptionMessages.subscriptionPlan),
            },
        }),
        columnHelper.display({
            id: 'cycle-count',
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(subscriptionMessages.cycleCount),
            },
            cell: ({ row }) => {
                if (!row.original.purchase_plan_id || !plansData) return '-';

                const plan = plansData.results.find((plan) => plan.id === row.original.purchase_plan_id);
                const durationValue = plan?.duration.value;
                const durationUnit = plan?.duration.unit;
                return durationValue && durationUnit
                    ? formatMessage(DURATION_MAP[durationUnit].count, {
                          value: durationValue,
                      })
                    : '-';
            },
        }),
        columnHelper.display({
            header: formatMessage(membershipMessages.allowedHours),
            enableHiding: true,
            id: 'allowed_hours',
            cell: ({ row }) => {
                if (
                    row.original.allowed_hours_start === START_FULL_DAY &&
                    row.original.allowed_hours_end === END_FULL_DAY
                )
                    return formatMessage(membershipMessages.fullDay);
                return (
                    <div className="flex flex-wrap">
                        <span className="whitespace-nowrap">{row.original.allowed_hours_start}</span>
                        <span> - </span>
                        <span className="whitespace-nowrap">{row.original.allowed_hours_end}</span>
                    </div>
                );
            },
        }),
        columnHelper.accessor('allowed_days', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: CellAllowedDays,
            meta: {
                title: formatMessage(membershipMessages.allowedDays),
                badgeOptions: badgeAllowedDaysOptions,
            },
        }),
        columnHelper.accessor('valid_until', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            cell: (props) => {
                const rowSubscriptionValidUntil = props.row.original.valid_until;
                const rowSubscriptionValidFrom = props.row.original.valid_from;

                const subscriptionStatus = () => {
                    const today = new Date();
                    if (today < new Date(rowSubscriptionValidFrom)) return 'upcoming';
                    if (today > new Date(rowSubscriptionValidFrom) && today < new Date(rowSubscriptionValidUntil))
                        return 'active';

                    return 'expired';
                };
                return (
                    <Badge
                        theme={badgeSubscriptionStatusOptions[subscriptionStatus()].theme}
                        className="gap-1.5 whitespace-nowrap"
                    >
                        {formatMessage(badgeSubscriptionStatusOptions[subscriptionStatus()].label)}
                    </Badge>
                );
            },
            meta: {
                inputType: INPUT_TYPE.MULTI_SELECT,
                title: formatMessage(subscriptionMessages.subscriptionStatus),
                badgeOptions: badgeSubscriptionStatusOptions,
                translatedInitialFilters: {
                    active: formatMessage(subscriptionMessages.subscriptionActive),
                    upcoming: formatMessage(subscriptionMessages.subscriptionUpcoming),
                    expired: formatMessage(subscriptionMessages.subscriptionExpired),
                },
            },
            id: 'validity_status',
        }),
        columnHelper.accessor('valid_from', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            enableColumnFilter: true,
            cell: CellDatetime,
            meta: {
                inputType: INPUT_TYPE.DATE,
                inlineDateFilter: true,
                dateFilter: {
                    afterDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
                    beforeDate: new Date(),
                },
                customDropdownMenuLabel: formatMessage(subscriptionMessages.subscriptionFilterByActivePeriod),
                title: formatMessage(subscriptionMessages.validFrom),
                filterLabel: formatMessage(subscriptionMessages.subscriptionActivePeriod),
            },
            id: 'inline_date_filters',
        }),
        columnHelper.accessor('valid_until', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            cell: CellDatetime,
            meta: {
                title: formatMessage(subscriptionMessages.validTo),
            },
        }),
        columnHelper.accessor('allowed_parkings', {
            header: DataTableColumnHeader,
            enableColumnFilter: true,
            cell: () => null,
            meta: {
                inputType: INPUT_TYPE.MULTI_SELECT,
                filterLabel: formatMessage(membershipMessages.allowedParkings),
                options: subParkings?.results.map((zone) => ({ value: zone.id, label: zone.name })),
            },
            id: 'parking_id',
        }),
    ];
    return (
        <DataTable
            columns={columns}
            data={data}
            refetch={refetch}
            search={search}
            sorting={sorting}
            filters={filters}
            pagination={pagination}
            isError={isError}
            isLoading={isLoading}
            rowSelection={rowSelection}
            getRowId={(row) => row.id.toString()}
            semantics={{
                title: formatMessage(subscriptionMessages.subscriptions),
                entryName: formatMessage(subscriptionMessages.subscription),
                entryNames: formatMessage(subscriptionMessages.subscriptions),
                placeholder: formatMessage(cardMessages.cardNumber),
                initialColumnVisibility: { parking_id: false },
            }}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
            }}
            dataTableBodyProps={{
                onRowClickCallback: (row) => {
                    void navigate(`/vehicles/subscriptions/${row.id}`);
                },
            }}
        />
    );
};

export default VehiclesSubscriptionsTable;
